/** Admin settings: people, pending invites, inviting people, appearance. */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, Navigate } from "react-router";
import { Avatar, ErrorNote, Fold, ROLE_LABEL } from "../components/ui";
import { api, ApiError } from "../lib/api";
import { loadInvites, revokeInvite, sendInvite, useStore, type Role } from "../lib/store";
import { applyTheme, readTheme, type Theme } from "../lib/theme";
import { AgentSettings } from "./settings-agents";
import { EventAgentSettings } from "./settings-events";
import { GithubSettings } from "./settings-github";

function since(iso: string | null): string {
  if (!iso) return "never";
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (min < 2) return "just now";
  if (min < 60) return `${min} minutes ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} ${h === 1 ? "hour" : "hours"} ago`;
  const d = Math.round(h / 24);
  return `${d} ${d === 1 ? "day" : "days"} ago`;
}

export function Settings() {
  const boot = useStore((s) => s.boot)!;
  const invites = useStore((s) => s.invites);
  const [theme, setTheme] = useState<Theme>(readTheme());

  useEffect(() => {
    void loadInvites();
  }, []);

  if (boot.workspace.role !== "admin") return <Navigate to="/" replace />;
  const people = [...boot.people].filter((p) => p.kind === "human").sort((a, b) => a.name.localeCompare(b.name));

  return (
    <main className="page">
      <h1>Settings</h1>
      <p className="lead">Agents, AI keys, GitHub, people, invites and appearance for {boot.workspace.name}.</p>
      <nav className="settings-nav" aria-label="Settings sections">
        <a href="#agents">Agents</a>
        <a href="#brief">What agents should know</a>
        <a href="#keys">AI keys</a>
        <a href="#events">Event agents</a>
        <Link to="/settings/usage">Usage and spending</Link>
        <a href="#github">GitHub</a>
        <a href="#invite">Invite people</a>
        <a href="#people">People</a>
      </nav>

      <AgentSettings />

      <EventAgentSettings />

      <GithubSettings />

      <StorageSettings />

      <Fold id="settings-invite" anchor="invite" title="Invite people" summary="Send an invite link by email, as a Member or a Guest.">
        <InviteForm />
      </Fold>

      <Fold id="settings-pending" title="Pending invites" summary={invites ? (invites.length ? `${invites.length} waiting: ${invites.map((i) => i.email).join(", ")}` : "No pending invites.") : undefined}>
        {!invites && <p className="muted-text">Loading</p>}
        {invites?.length === 0 && <p className="muted-text">No pending invites.</p>}
        {invites && invites.length > 0 && (
          <ul className="rows">
            {invites.map((i) => (
              <li key={i.id}>
                <div>
                  <b>{i.email}</b>
                  <small>
                    {ROLE_LABEL[i.role]}
                    {i.rooms.length > 0 && ` · ${i.rooms.map((r) => `#${r.name}`).join(", ")}`}
                    {i.invitedBy && ` · sent by ${i.invitedBy}`} · expires {new Date(i.expiresAt).toLocaleDateString()}
                  </small>
                </div>
                <button className="btn ghost small" type="button" onClick={() => confirm(`Revoke the invite for ${i.email}?`) && void revokeInvite(i.id)}>
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        )}
      </Fold>

      <Fold id="settings-people" anchor="people" title="People" summary={`${people.length} ${people.length === 1 ? "person" : "people"}: ${people.map((p) => p.name).join(", ")}`}>
        <ul className="rows">
          {people.map((p) => (
            <li key={p.id}>
              <Avatar person={p} size={30} showOnline />
              <div>
                <b>{p.name}</b>
                <small>
                  {p.email} · last seen {p.online ? "now" : since(p.lastSeenAt)}
                </small>
              </div>
              <span className="chip">{ROLE_LABEL[p.role]}</span>
            </li>
          ))}
        </ul>
      </Fold>

      <section className="card-block">
        <h2>Appearance</h2>
        <label className="field inline-field">
          <span>Theme on this device</span>
          <select
            value={theme}
            onChange={(e) => {
              const t = e.target.value as Theme;
              setTheme(t);
              applyTheme(t);
            }}
          >
            <option value="system">Follow the system</option>
            <option value="light">Light</option>
            <option value="dark">Dark</option>
          </select>
        </label>
      </section>
    </main>
  );
}

function InviteForm() {
  const allRooms = useStore((s) => s.boot?.rooms);
  const rooms = useMemo(() => (allRooms ?? []).filter((r) => r.kind === "room"), [allRooms]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Exclude<Role, "admin">>("member");
  const [roomIds, setRoomIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const status = await sendInvite({ email, role, roomIds });
      setNote(status === "added" ? `${email} is already here, so they were added to the rooms.` : `Invite sent to ${email}. The link works once and expires in 7 days.`);
      setEmail("");
      setRoomIds([]);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not send the invite.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="form" onSubmit={submit}>
      <ErrorNote error={error} />
      {note && <div className="notice ok">{note}</div>}
      <label className="field">
        <span>Email</span>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="name@company.com" />
      </label>
      <label className="field">
        <span>Role</span>
        <select value={role} onChange={(e) => setRole(e.target.value as "member" | "guest")}>
          <option value="member">Member: sees every public room and can create rooms</option>
          <option value="guest">Guest: sees only the rooms they are invited to</option>
        </select>
      </label>
      {rooms.length > 0 && (
        <fieldset className="field">
          <legend>Add to rooms (optional)</legend>
          <div className="room-checks">
            {rooms.map((r) => (
              <label key={r.id} className="check">
                <input type="checkbox" checked={roomIds.includes(r.id)} onChange={(e) => setRoomIds((ids) => (e.target.checked ? [...ids, r.id] : ids.filter((x) => x !== r.id)))} />
                <span>#{r.name}</span>
              </label>
            ))}
          </div>
        </fieldset>
      )}
      <div className="actions">
        <button className="btn primary" type="submit" disabled={busy || !email || (role === "guest" && roomIds.length === 0)}>
          Send invite
        </button>
      </div>
      {role === "guest" && roomIds.length === 0 && <small className="muted-text">Pick at least one room for a Guest.</small>}
    </form>
  );
}

/** Files in messages: how much the workspace stores, of what it may. */
function StorageSettings() {
  const [use, setUse] = useState<{ files: number; bytes: number; limitBytes: number } | null>(null);
  useEffect(() => {
    api<{ files: number; bytes: number; limitBytes: number }>("/api/settings/storage").then(setUse, () => setUse(null));
  }, []);
  if (!use) return null;
  const mb = (n: number) => (n / 1024 / 1024).toFixed(n < 10 * 1024 * 1024 ? 1 : 0);
  return (
    <section className="card-block" id="files">
      <h2>Files</h2>
      <p className="muted-text">
        {use.files} {use.files === 1 ? "file" : "files"} in messages, {mb(use.bytes)} MB of {Math.round(use.limitBytes / 1024 / 1024 / 1024)} GB. They are kept with the messages and backed up every night.
      </p>
    </section>
  );
}
